import { v1Get, v1Post } from '@/services/v1Api';

/**
 * Image Suggestion Service
 * Handles API calls for the image suggestion system
 */
export class ImageSuggestionService {
    /**
     * Get image suggestions for an asset
     * @param {string|number} assetId - The asset ID
     * @param {number} limit - Maximum number of suggestions to return (default: 5)
     * @returns {Promise<Object>} API response with suggestions
     */
    static async getSuggestions(assetId, limit = 5) {
        try {
            const { data } = await v1Get(`/api/v1/local-assets/${assetId}/image-suggestions`, { limit });
            if (data?.suggestions) {
                return { suggestions: data.suggestions };
            }
            return {
                suggestions: Array.isArray(data) ? data : [],
            };
        } catch (error) {
            console.error('Error fetching image suggestions:', error);
            throw error;
        }
    }

    /**
     * Get usage stats for an image
     * @param {string} imageUrl - The image URL to get stats for
     * @returns {Promise<Object>} API response with usage stats
     */
    static async getUsageStats(imageUrl) {
        try {
            const { data } = await v1Get('/api/v1/image-usage-stats', { image_url: imageUrl });
            return typeof data === 'object' && data ? data : { data };
        } catch (error) {
            console.error('Error fetching image usage stats:', error);
            throw error;
        }
    }

    /**
     * Check if image suggestions are supported for an asset type
     * @param {string} assetType - The asset type to check
     * @returns {boolean} Whether suggestions are supported
     */
    static isSupportedAssetType(assetType) {
        const supportedTypes = ['cables'];
        return supportedTypes.includes(assetType);
    }

    /**
     * Apply a suggested image to an asset
     * @param {string|number} assetId - The asset ID
     * @param {string} imageUrl - The image URL to apply
     * @param {string|number} sourceCableId - The source cable ID
     * @returns {Promise<Object>} API response with success, message, image_url, and image_source
     */
    static async applySuggestedImage(assetId, imageUrl, sourceCableId) {
        try {
            const { data } = await v1Post(`/api/v1/local-assets/${assetId}/apply-suggested-image`, {
                image_url: imageUrl,
                source_cable_id: sourceCableId
            });
            return {
                success: true,
                ...(typeof data === 'object' && data ? data : { data }),
            };
        } catch (error) {
            console.error('Error applying suggested image:', error);
            throw error;
        }
    }

    /**
     * Track analytics event for image suggestions
     * @param {string} event - The event name
     * @param {Object} data - Additional event data
     */
    static trackEvent(event, data = {}) {
        const events = {
            'image_suggestions_modal_opened': 'Image suggestions modal opened',
            'image_suggestion_selected': 'Image suggestion selected',
            'image_suggestion_used': 'Image suggestion used',
            'new_image_uploaded': 'New image uploaded'
        };

        if (events[event]) {
            console.log(`Analytics: ${events[event]}`, data);
        }
    }
}

export default ImageSuggestionService;
